/**
 * `/admin` — the operator's page. The deployment's numbers, and the
 * invitations, behind the admin token rather than any account.
 *
 * There is no admin login in the usual sense because there is no admin user:
 * `ADMIN_TOKEN` is the whole credential, exactly as it is for `npm run invite`.
 * The token is asked for once and kept for this tab (`sessionStorage`), never
 * in localStorage and never in a URL, and "Forget token" throws it away.
 */
import type { AdminStats, Invite } from '@circle/shared';
import { inviteLink } from '@circle/shared';
import { ApiError, adminApi } from '../api';
import type { PageContext, Teardown } from '../router';
import { button, copyRow, el, setChildren, textField, withBusy, type Child } from '../../ui/dom';
import { confirmDialog } from '../../ui/dialog';
import { loadingState } from '../../ui/states';
import { errorMessage, toast, toastError } from '../../ui/toast';

const TOKEN_KEY = 'circle:admin-token';

function rememberedToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function remember(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* a tab that cannot keep it asks again next time */
  }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

const formatCount = (n: number): string => n.toLocaleString('en-GB');

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export async function renderAdminPage(ctx: PageContext): Promise<Teardown> {
  const col = el('div', { class: 'sheet__col' });
  ctx.host.replaceChildren(el('div', { class: 'sheet' }, col));

  let token = rememberedToken();
  if (!token) {
    setChildren(col, gate());
    return;
  }
  await open(token);

  /** Ask for the token. The only way onto the page, and the way back after "Forget". */
  function gate(message?: string): Child[] {
    const field = textField({
      label: 'Admin token',
      name: 'admin-token',
      type: 'password',
      autocomplete: 'off',
      hint: 'The ADMIN_TOKEN secret of this deployment — the same one npm run invite uses.',
    });
    if (message) field.setError(message);
    const go = button(
      'Open',
      () =>
        void withBusy(go, async () => {
          const value = field.input.value.trim();
          if (!value) {
            field.setError('Paste the token.');
            field.input.focus();
            return;
          }
          try {
            await adminApi.stats(value);
          } catch (err) {
            field.setError(gateError(err));
            return;
          }
          token = value;
          remember(value);
          await open(value);
        }),
      { class: 'btn--primary' },
    );
    field.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        go.click();
      }
    });
    return [
      el('h1', { text: 'Operator' }),
      el('p', {
        class: 'lede',
        text: 'The deployment’s numbers and its invitations. This is not an account: the token is the key, and it stays in this tab only.',
      }),
      field.root,
      el('div', { class: 'row' }, go, el('a', { class: 'btn btn--quiet', href: '/', text: 'Back' })),
    ];
  }

  function gateError(err: unknown): string {
    if (err instanceof ApiError && err.status === 401) return 'That is not the token.';
    if (err instanceof ApiError && err.status === 404) {
      return 'This deployment has no admin API: ADMIN_TOKEN is not set on the worker.';
    }
    return errorMessage(err, 'Could not reach the worker.');
  }

  async function open(current: string): Promise<void> {
    setChildren(col, [loadingState('Counting')]);
    let stats: AdminStats;
    let invites: Invite[];
    try {
      [stats, invites] = await Promise.all([adminApi.stats(current), adminApi.listInvites(current)]);
    } catch (err) {
      // A token that stopped working — rotated on the worker, say — sends the
      // page back to the gate with the reason, rather than to a dead dashboard.
      remember(null);
      token = null;
      setChildren(col, gate(gateError(err)));
      return;
    }
    setChildren(col, dashboard(current, stats, invites));
  }

  function dashboard(current: string, stats: AdminStats, invites: Invite[]): Child[] {
    const refresh = button('Refresh', () => void withBusy(refresh, () => open(current)), { class: 'btn--small' });
    const forget = button(
      'Forget token',
      () => {
        remember(null);
        token = null;
        setChildren(col, gate());
      },
      { class: 'btn--small btn--quiet' },
    );

    const storageBytes = stats.storage.databaseBytes ?? stats.images.bytes;
    const share = storageBytes / stats.storage.databaseLimitBytes;
    const warn = share > 0.7;

    return [
      el(
        'div',
        { class: 'spread' },
        el('h1', { text: 'Operator' }),
        el('div', { class: 'row' }, refresh, forget),
      ),
      el('p', {
        class: 'small muted',
        text: `Counted ${new Date(stats.generatedAt).toLocaleString('en-GB')}. Nothing here names a person or a token.`,
      }),

      section('People', [
        tile(stats.users.total, 'accounts'),
        tile(stats.users.withPasskey, 'with a passkey'),
        tile(stats.users.withGithub, 'with GitHub'),
        tile(stats.users.liveSessions, 'live sessions'),
      ]),

      section('Rings', [
        tile(stats.calendars.total, 'calendars'),
        tile(stats.calendars.claimed, 'claimed by an account'),
        tile(stats.calendars.shared, 'shared by link'),
        tile(stats.calendars.createdLast7Days, 'made this week'),
        tile(stats.calendars.createdLast30Days, 'made this month'),
      ]),

      section('Circles', [
        tile(stats.circles.tags, 'circles'),
        tile(stats.circles.shared, 'with a link of their own'),
        tile(stats.circles.views, 'saved views'),
      ]),

      section('Entries and sources', [
        tile(stats.events.total, 'entries'),
        tile(stats.events.withPhoto, 'with a photo'),
        tile(stats.events.orphaned, 'dropped by their feed', stats.events.orphaned > 0),
        tile(stats.sources.ics, 'ICS feeds'),
        tile(stats.sources.icsFailing, 'feeds failing', stats.sources.icsFailing > 0),
        tile(stats.sources.collect, 'collect links'),
        tile(stats.sources.pendingSubmissions, 'awaiting approval'),
      ]),

      section(
        'Photos and storage',
        [
          tile(stats.images.count, 'photos stored'),
          tile(formatBytes(stats.images.bytes), 'of photo bytes'),
          tile(stats.images.orphaned, 'pointed at by nothing', stats.images.orphaned > 0),
          tile(`${stats.images.uploadsToday} / ${stats.quotas.maxUploadsPerDay}`, 'uploads today', stats.images.uploadsToday >= stats.quotas.maxUploadsPerDay * 0.8),
        ],
        [
          el(
            'div',
            { class: 'stack' },
            el(
              'div',
              { class: 'spread small muted' },
              el('span', {
                text: stats.storage.databaseBytes == null
                  ? 'Database size, counted from the photos alone'
                  : 'Database on disk',
              }),
              el('span', {
                class: 'mono',
                text: `${formatBytes(storageBytes)} of ${formatBytes(stats.storage.databaseLimitBytes)} (${(share * 100).toFixed(share < 0.01 ? 2 : 1)}%)`,
              }),
            ),
            meter(share, warn),
            el('div', {
              class: 'field__hint',
              text: `Ceilings: ${formatBytes(stats.quotas.maxImageBytes)} a photo, ${stats.quotas.maxImagesPerCalendar} photos a ring, ${stats.quotas.maxUploadsPerIpDay} uploads a day per submitter, ${stats.quotas.maxUploadsPerDay} a day in all.`,
            }),
          ),
        ],
      ),

      el(
        'section',
        { class: 'section stack' },
        el('h2', { text: 'The heaviest rings' }),
        stats.largestCalendars.length
          ? el('div', { class: 'admin-table-wrap' }, calendarsTable(stats.largestCalendars))
          : el('div', { class: 'empty', text: 'No calendars yet.' }),
      ),

      el(
        'section',
        { class: 'section stack' },
        el('h2', { text: 'Invitations' }),
        el('div', { class: 'stats' }, tile(stats.invites.live, 'live codes'), tile(stats.invites.usesSpent, 'uses spent'), tile(stats.invites.total, 'minted in all')),
        mintForm(current),
        invites.length
          ? el('div', { class: 'admin-table-wrap' }, invitesTable(current, invites))
          : el('div', { class: 'empty', text: 'Nothing minted yet.' }),
      ),
    ];
  }

  function section(title: string, tiles: Child[], extra: Child[] = []): HTMLElement {
    return el('section', { class: 'section stack' }, el('h2', { text: title }), el('div', { class: 'stats' }, ...tiles), ...extra);
  }

  function tile(value: number | string, label: string, warn = false): HTMLElement {
    return el(
      'div',
      { class: 'stat', 'data-warn': warn ? 'true' : 'false' },
      el('span', { class: 'stat__value', text: typeof value === 'number' ? formatCount(value) : value }),
      el('span', { class: 'stat__label', text: label }),
    );
  }

  function meter(share: number, warn: boolean): HTMLElement {
    const fill = el('div', { class: 'meter__fill' });
    fill.style.width = `${Math.min(100, Math.max(0.5, share * 100))}%`;
    return el(
      'div',
      {
        class: 'meter',
        role: 'meter',
        'aria-valuemin': '0',
        'aria-valuemax': '100',
        'aria-valuenow': (share * 100).toFixed(1),
        'aria-label': 'Database used, as a share of the free tier',
        'data-warn': warn ? 'true' : 'false',
      },
      fill,
    );
  }

  function calendarsTable(rows: AdminStats['largestCalendars']): HTMLElement {
    const head = el(
      'tr',
      {},
      el('th', { text: 'Ring' }),
      el('th', { text: 'Made' }),
      el('th', { text: 'Held by' }),
      el('th', { text: 'Sharing' }),
      el('th', { class: 'num', text: 'Entries' }),
      el('th', { class: 'num', text: 'Photos' }),
      el('th', { class: 'num', text: 'Photo bytes' }),
    );
    const body = rows.map((r) =>
      el(
        'tr',
        {},
        el('td', { class: 'name', text: r.name }),
        el('td', { text: formatDate(r.createdAt) }),
        el('td', { text: r.claimed ? 'an account' : 'its owner link' }),
        el('td', { text: r.visibility === 'private' ? 'private' : 'by link' }),
        el('td', { class: 'num', text: formatCount(r.events) }),
        el('td', { class: 'num', text: formatCount(r.images) }),
        el('td', { class: 'num', text: formatBytes(r.imageBytes) }),
      ),
    );
    return el('table', { class: 'admin-table' }, el('thead', {}, head), el('tbody', {}, ...body));
  }

  /** Mint codes here rather than at a terminal. The codes are shown once, as they are everywhere. */
  function mintForm(current: string): HTMLElement {
    const count = textField({ label: 'How many', name: 'count', value: '1', inputmode: 'numeric' });
    const uses = textField({ label: 'Uses each', name: 'uses', value: '1', inputmode: 'numeric' });
    const days = textField({ label: 'Expires in days', name: 'days', placeholder: 'never', inputmode: 'numeric' });
    const note = textField({ label: 'Note', name: 'note', placeholder: 'friends', maxlength: '200' });
    const minted = el('div', { class: 'stack' });
    const mint = button(
      'Mint',
      () =>
        void withBusy(mint, async () => {
          const whole = (field: typeof count, min: number): number | undefined | null => {
            const raw = field.input.value.trim();
            if (!raw) return undefined;
            const value = Number(raw);
            if (!Number.isInteger(value) || value < min) {
              field.setError(`A whole number, ${min} or more.`);
              return null;
            }
            field.setError(null);
            return value;
          };
          const body = {
            count: whole(count, 1),
            maxUses: whole(uses, 1),
            expiresInDays: whole(days, 1),
            note: note.input.value.trim() || undefined,
          };
          if (Object.values(body).includes(null)) return;
          try {
            const codes = await adminApi.mintInvites(current, body as Parameters<typeof adminApi.mintInvites>[1]);
            setChildren(minted, [
              el('p', { class: 'small muted', text: `${codes.length === 1 ? 'One code' : `${codes.length} codes`} minted. This is the only time they are shown.` }),
              ...codes.map((c) => copyRow(inviteLink(c.code, location.origin), 'Copy link')),
            ]);
            toast('Minted.');
          } catch (err) {
            toastError(err, 'Nothing was minted.');
          }
        }),
      { class: 'btn--primary btn--small' },
    );
    return el(
      'div',
      { class: 'stack' },
      el('h3', { text: 'Mint codes' }),
      el('div', { class: 'admin-mint' }, count.root, uses.root, days.root, note.root, el('div', { class: 'row' }, mint)),
      minted,
    );
  }

  function invitesTable(current: string, invites: Invite[]): HTMLElement {
    const now = Date.now();
    const state = (i: Invite): string => {
      if (i.revokedAt) return 'revoked';
      if (i.expiresAt && Date.parse(i.expiresAt) < now) return 'expired';
      if (i.uses >= i.maxUses) return 'used up';
      return 'live';
    };
    const head = el(
      'tr',
      {},
      el('th', { text: 'Minted' }),
      el('th', { text: 'By' }),
      el('th', { text: 'Note' }),
      el('th', { class: 'num', text: 'Uses' }),
      el('th', { text: 'Expires' }),
      el('th', { text: 'State' }),
      el('th', { text: '' }),
    );
    const body = invites.map((i) => {
      const live = state(i) === 'live';
      const revoke = button(
        'Revoke',
        async () => {
          const yes = await confirmDialog({
            title: 'Revoke this code?',
            description: 'Whatever was already made with it stays. The code itself stops working.',
            confirmLabel: 'Revoke',
            danger: true,
          });
          if (!yes) return;
          try {
            await adminApi.revokeInvite(current, i.id);
            toast('Revoked.');
            await open(current);
          } catch (err) {
            toastError(err);
          }
        },
        { class: 'btn--small btn--quiet' },
      );
      return el(
        'tr',
        {},
        el('td', { text: formatDate(i.createdAt) }),
        el('td', { text: i.createdBy ? 'an owner' : 'the operator' }),
        el('td', { class: 'name', text: i.note ?? '' }),
        el('td', { class: 'num', text: `${i.uses} / ${i.maxUses}` }),
        el('td', { text: i.expiresAt ? formatDate(i.expiresAt) : 'never' }),
        el('td', { text: state(i) }),
        el('td', {}, live ? revoke : null),
      );
    });
    return el('table', { class: 'admin-table' }, el('thead', {}, head), el('tbody', {}, ...body));
  }
}
